/**
 * External Access Layout
 * Minimal layout for external users with simple header and no sidebar
 */

import React from 'react';
import { Box, AppBar, Toolbar, Typography, Button, Container, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { logoutRequest } from '../redux/modules/auth/authSlice';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import features from '../config/features';

const ExternalReferrerLayout = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { userData } = useSelector((state) => state.auth);

  const handleLogout = () => {
    dispatch(logoutRequest());
    localStorage.clear();
    navigate('/login');
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: 'grey.50' }}>
      {/* Header */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          bgcolor: 'primary.main',
          borderBottom: '1px solid',
          borderColor: 'primary.dark',
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 56, sm: 64 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <img
              alt="Logo"
              src={new URL('../assets/images/main-logo.png', import.meta.url).href}
              style={{ height: isMobile ? 32 : 40, marginRight: 12 }}
            />
            <Box>
              <Typography
                variant={isMobile ? 'body1' : 'h6'}
                component="div"
                sx={{ color: 'white', fontWeight: 600, lineHeight: 1.2 }}
              >
                External Portal
              </Typography>
              {!isMobile && (
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                  {features.client?.name || 'Voxxil HIS'}
                </Typography>
              )}
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 } }}>
            {!isMobile && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonIcon sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 20 }} />
                <Typography variant="body2" sx={{ color: 'white' }}>
                  {userData?.firstName} {userData?.lastName}
                </Typography>
              </Box>
            )}
            <Button
              color="inherit"
              onClick={handleLogout}
              startIcon={!isMobile && <LogoutIcon />}
              sx={{
                color: 'white',
                textTransform: 'none',
                fontWeight: 500,
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.1)',
                },
              }}
            >
              {isMobile ? <LogoutIcon /> : 'Logout'}
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Spacer for fixed header */}
      <Toolbar sx={{ minHeight: { xs: 56, sm: 64 } }} />

      {/* Main Content */}
      <Container
        maxWidth="lg"
        sx={{
          flexGrow: 1,
          py: { xs: 3, sm: 4 },
          px: { xs: 2, sm: 3 },
        }}
      >
        {children}
      </Container>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          py: 2,
          px: 3,
          mt: 'auto',
          bgcolor: 'white',
          borderTop: '1px solid',
          borderColor: 'grey.200',
        }}
      >
        <Typography variant="body2" color="text.secondary" align="center">
          External Access Portal &bull; {features.client?.name || 'Voxxil HIS'}
        </Typography>
      </Box>
    </Box>
  );
};

export default ExternalReferrerLayout;
